import {startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ApplyTask, runApplyTasks} from "../../run-apply-tasks";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayEventRuntimeInfo} from "./event-definitions";
import {prepareDiffEvent} from "./prepare-diff-event";

export class EventRemoved implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'EventRemoved';
  public readonly operation: PlayshotChangedOperation = 'removed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.event;
  public readonly diff: PlayshotChangedDiff;

  constructor(name: string, nodeId: string, event: PlayEventRuntimeInfo, oldPlayshot: Playshot) {

    // @TODO: handle aggregate event
    const applyRules = undefined; /* = playshot.playConfig.eventReducers[event.desc.name]; */

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: null,
      old: prepareDiffEvent(event, applyRules),
    };
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.event, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the event cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const similarNodes = elementsTree.getSimilarElements(existingNode);

    const applyTasks: ApplyTask[] = [
      {
        name: "Focus event to remove",
        func: async () => {
          graph.focusNode(existingNode);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      },
      {
        name: "Remove event",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(existingNode);

              resolve({
                cody: `I've removed the event card for "${nodeLabel(this.name)}" from the board`
              });

              return
            }, 1500);
          })
        }
      }
    ];

    similarNodes.forEach(sN => {
      applyTasks.push({
        name: "Focus event to remove",
        func: async () => {
          graph.focusNode(sN);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      });
      applyTasks.push({
        name: "Remove event",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(sN);

              resolve({
                cody: ``,
                type: CodyResponseType.Empty
              });
            }, 1500);
          })
        }
      })
    })

    return runApplyTasks(applyTasks)
  }
}
